import React, { useEffect, useState } from "react";
import Loader from "../../components/Loader";
import Calling from "../../api/Calling";
import { toast } from "react-toastify";
import NotFound from "../../components/ul/NotFound";
import formatDateTime from "../../components/formatDateTime";
import { FaUser, FaTrophy, FaMedal, FaCrown } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import img1 from "../../Image/Profile.png";

const LeaderBoard = () => {
  const [loader, setLoader] = useState(true);
  const [data, setData] = useState([]);

  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      const response = await Calling.GetLeaderBoard(1, 10);

      if (response?.status) {
        setData(response?.data || []);
      } else {
        toast.error(response?.message);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleUser = (id) => {
    navigate(`/manage/user/all/info/${id}`);
  };

  const getRankIcon = (index) => {
    switch (index) {
      case 0:
        return <FaCrown className="text-yellow-400 text-xl" />;
      case 1:
        return <FaMedal className="text-gray-400 text-xl" />;
      case 2:
        return <FaMedal className="text-amber-600 text-xl" />;
      default:
        return (
          <span className="text-lg font-bold text-gray-600">#{index + 1}</span>
        );
    }
  };

  const getRankBadge = (index) => {
    const badges = [
      "bg-gradient-to-r from-yellow-400 to-yellow-600",
      "bg-gradient-to-r from-gray-300 to-gray-500",
      "bg-gradient-to-r from-amber-500 to-amber-700",
    ];
    return index < 3
      ? badges[index]
      : "bg-gradient-to-r from-blue-500 to-blue-600";
  };

  const getInitials = (name) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  return (
    <>
      {loader ? (
        <Loader loader={loader} />
      ) : (
        <div className="w-full md:ml-[4.4rem] px-3 sm:px-4 lg:px-6 mt-16">
          <div className="max-w-7xl mx-auto py-4">
            {/* Header Section */}
            <div className="text-center mb-8 md:mb-12">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full mb-4 shadow-lg">
                <FaTrophy className="text-4xl text-white" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                Leaderboard
              </h1>
              <p className="text-gray-600 text-lg">
                Top performers of the season
              </p>
            </div>

            {/* Top 3 Podium (Medium Compact) */}
            {data.length >= 3 && (
              <div className="grid grid-cols-3 gap-4 mb-8 max-w-3xl mx-auto">
                {/* 2nd Place */}
                <div className="flex flex-col items-center pt-4">
                  <div className="relative mb-2">
                    <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-gradient-to-br from-gray-300 to-gray-500 flex items-center justify-center text-white text-xl md:text-2xl font-bold shadow-lg">
                      {data[1].u_profile ? (
                        <img
                          src={data[1].u_profile}
                          alt="Profile"
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        getInitials(data[1].u_name)
                      )}
                    </div>
                    <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-1 shadow-md">
                      <FaMedal className="text-gray-400 text-lg" />
                    </div>
                  </div>

                  <h3 className="font-semibold text-sm text-gray-800 text-center mb-1">
                    {data[1].u_name}
                  </h3>
                  <div className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-white px-2 py-0.5 rounded-full text-xs font-semibold">
                    {data[1].u_coin} 🪙
                  </div>

                  <div className="mt-3 bg-gradient-to-br from-gray-300 to-gray-500 w-full h-16 md:h-20 rounded-t-lg shadow-md flex items-center justify-center">
                    <span className="text-white text-2xl md:text-3xl font-bold">
                      2
                    </span>
                  </div>
                </div>

                {/* 1st Place */}
                <div className="flex flex-col items-center">
                  <div className="relative mb-2">
                    <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center text-white text-2xl md:text-3xl font-bold shadow-2xl ring-4 ring-yellow-200">
                      {data[0].u_profile ? (
                        <img
                          src={data[0].u_profile}
                          alt="Profile"
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        getInitials(data[0].u_name)
                      )}
                    </div>
                    <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-1 shadow-md">
                      <FaCrown className="text-yellow-400 text-xl" />
                    </div>
                  </div>

                  <h3 className="font-semibold text-sm md:text-base text-gray-800 text-center mb-1">
                    {data[0].u_name}
                  </h3>
                  <div className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-white px-3 py-1 rounded-full text-xs md:text-sm font-semibold shadow-lg">
                    {data[0].u_coin} 🪙
                  </div>

                  <div className="mt-3 bg-gradient-to-br from-yellow-400 to-yellow-600 w-full h-20 md:h-24 rounded-t-lg shadow-md flex items-center justify-center">
                    <span className="text-white text-3xl md:text-4xl font-bold">
                      1
                    </span>
                  </div>
                </div>

                {/* 3rd Place */}
                <div className="flex flex-col items-center pt-6">
                  <div className="relative mb-2">
                    <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center text-white text-xl md:text-2xl font-bold shadow-lg">
                      {data[2].u_profile ? (
                        <img
                          src={data[2].u_profile}
                          alt="Profile"
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        getInitials(data[2].u_name)
                      )}
                    </div>
                    <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-1 shadow-md">
                      <FaMedal className="text-amber-600 text-lg" />
                    </div>
                  </div>

                  <h3 className="font-semibold text-sm text-gray-800 text-center mb-1">
                    {data[2].u_name}
                  </h3>
                  <div className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-white px-2 py-0.5 rounded-full text-xs font-semibold">
                    {data[2].u_coin} 🪙
                  </div>

                  <div className="mt-3 bg-gradient-to-br from-amber-500 to-amber-700 w-full h-14 md:h-18 rounded-t-lg shadow-md flex items-center justify-center">
                    <span className="text-white text-2xl md:text-3xl font-bold">
                      3
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Full Leaderboard Table */}
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                    <tr>
                      <th className="p-4 text-left text-sm font-semibold">
                        Rank
                      </th>
                      <th className="p-4 text-left text-sm font-semibold">
                        Player
                      </th>
                      <th className="p-4 text-left text-sm font-semibold hidden md:table-cell">
                        Email
                      </th>
                      <th className="p-4 text-left text-sm font-semibold">
                        Coins
                      </th>
                      <th className="p-4 text-left text-sm font-semibold hidden lg:table-cell">
                        Joined
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.length > 0 ? (
                      data?.map((user, index) => (
                        <tr
                          key={user._id}
                          className="border-b border-gray-100 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 transition-all duration-300 transform cursor-pointer"
                          onClick={() => handleUser(user?._id)}
                        >
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              {getRankIcon(index)}
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div
                                className={`w-12 h-12 rounded-full ${getRankBadge(
                                  index
                                )} flex items-center justify-center text-white font-bold shadow-md`}
                              >
                                {user.u_profile ? (
                                  <img
                                    src={user.u_profile}
                                    alt="Profile"
                                    className="w-full h-full rounded-full object-cover"
                                  />
                                ) : (
                                  getInitials(user.u_name)
                                )}
                              </div>
                              <div>
                                <p className="font-semibold text-gray-800">
                                  {user.u_name}
                                </p>
                                <p className="text-xs text-gray-500 md:hidden">
                                  {user.u_email}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="p-4 text-gray-600 hidden md:table-cell">
                            {user.u_email}
                          </td>
                          <td className="p-4">
                            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-100 to-yellow-200 px-3 py-1.5 rounded-full">
                              <span className="font-bold text-yellow-700">
                                {user.u_coin}
                              </span>
                              <span>🪙</span>
                            </div>
                          </td>
                          <td className="p-4 text-gray-500 text-sm hidden lg:table-cell">
                            {formatDateTime(user.createdAt)}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6">
                          <NotFound />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LeaderBoard;
